import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { throttle } from "lodash";

import { FilterMatchMode, FilterService } from "primereact/api";
import { Chip } from "primereact/chip";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTable, DataTableFilterMeta, DataTableStateEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";

import { faFileDownload, faXmark } from "@fortawesome/pro-solid-svg-icons";

import { IGlossaryFileSupportExtended } from "../../../../../features/translation/TranslationHeader/GlossarySelection/GlossarySelection.types";
import { translationKeys } from "../../../../../models/app-translation-keys";
import { useGlossaryService } from "../../../../../services/ServiceHooks";
import { useStore } from "../../../../../stores/StoreContext";
import { ColumnActionIcon } from "../../../../Shared/ColumnActionIcon/ColumnActionIcon";
import { confirmAction } from "../../../../Shared/Functions";
import { TableSkeleton } from "../../../../Shared/LoadingSkeletons/TableSkeleton/TableSkeleton";
import EditSecurityGroupButton from "./UpdateGlossaryButton/UpdateGlossaryButton";

import "./GlossaryTable.scss";

import { faTrash } from "@fortawesome/pro-solid-svg-icons";

interface IGlossaryTableProps {
  glossaryInfos: IGlossaryFileSupportExtended[];
  isLoading: boolean;
  onReloadTable: () => void;
  onDeleteEntry: (entry: IGlossaryFileSupportExtended) => void;
}

const GlossaryTable: FunctionComponent<IGlossaryTableProps> = (props: IGlossaryTableProps) => {
  const { t } = useTranslation();
  const glossaryService = useGlossaryService();
  const { userStore } = useStore();

  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([]);
  const glossaryTranslationKeys = translationKeys.adminArea.glossary;

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  // Filters and sorting
  const [filters, setFilters] = useState<DataTableFilterMeta | undefined>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    supportedLanguages: {
      value: null,
      matchMode: "SupportedLanguagesMatchMode" as any,
    },
  });

  FilterService.register("SupportedLanguagesMatchMode", (supportedLanguages: string[], filterValue: string[]) => {
    // Custom registered filter match mode since filterFunction prop on Column doesn't work as expected
    // GitHub issue and working example: https://github.com/primefaces/primereact/issues/3325
    if (!supportedLanguages?.length || !filterValue?.length) {
      return true;
    }

    return supportedLanguages.some((supportedLanguage: string) => filterValue.includes(supportedLanguage));
  });

  const downloadGlossaryFile = async (rowData: IGlossaryFileSupportExtended) => {
    const blob = await glossaryService.downloadGlossaryFile(rowData.glossaryId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t(translationKeys.adminArea.glossary.glossaryDownloadName, { name: rowData.name })}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteGlossary = async (rowData: IGlossaryFileSupportExtended) => {
    await glossaryService.deleteGlossary(rowData.glossaryId);
    props.onDeleteEntry(rowData);
  };

  const supportedLanguagesTemplate = (supportedLanguages: string[]) => (
    <div className="supported-languages-chips">
      {supportedLanguages.map(lang => (
        <Chip key={lang} label={lang} style={{ fontSize: "12px" }} />
      ))}
    </div>
  );

  const SupportedLanguagesFilterElement = (options: ColumnFilterElementTemplateOptions) => (
    <MultiSelect
      value={options.value ?? []}
      onChange={e => options.filterApplyCallback(e.value)}
      options={supportedLanguages}
      placeholder={t(translationKeys.adminArea.appRoles.actions.filterRoles)}
      className="w-full md:w-20rem"
      maxSelectedLabels={2}
    />
  );

  useEffect(() => {
    const languages = props.glossaryInfos
      .flatMap(info => info.supportedLanguages)
      .filter((value, index, self) => self.indexOf(value) === index);
    setSupportedLanguages(languages);
  }, [props.glossaryInfos]);

  const clearFilter = () => {
    onGlobalFilterChange("");
  };

  const onGlobalFilterChange = (value: string) => {
    const _filters = { ...filters };
    (_filters["global"] as any).value = String(value).trim();
    setFilters(_filters);
  };

  const throttleGlobalFilter = throttle(onGlobalFilterChange, 200);

  const tableHeaderMemo = useMemo(() => {
    return (
      <div className="flex justify-content-end">
        <div className="p-icon-field p-icon-field-left p-icon-field-right">
          <i className="pi pi-search p-input-icon" style={{ width: "fit-content" }} />

          <InputText
            value={globalFilterValue}
            onChange={e => {
              setGlobalFilterValue(e.target.value);
              throttleGlobalFilter(e.target.value);
            }}
            placeholder={t(translationKeys.actions.filterTable)}
            className="pr-5"
          />

          {!!globalFilterValue?.length && (
            <i
              className="pi pi-times cursor-pointer p-link p-input-icon"
              onClick={clearFilter}
              aria-label={t(translationKeys.actions.clearFilter)}
              onKeyDown={e => e.key === "Enter" && clearFilter()}
              tabIndex={0}
              role="button"
            />
          )}
        </div>
      </div>
    );
  }, [globalFilterValue, userStore.currentLanguage]);

  const glossaryTableMemo = useMemo(() => {
    if (props.isLoading) {
      return <TableSkeleton rows={2} columns={5} />;
    }

    return (
      <div className="glossary-table">
        <DataTable
          key={`${filters ? Object.keys(filters).length : 0}-${userStore.currentLanguage}`}
          header={tableHeaderMemo}
          value={props.glossaryInfos}
          paginator
          scrollable
          scrollHeight="25rem"
          sortField="name"
          sortOrder={-1}
          rowHover
          rows={10}
          resizableColumns
          rowsPerPageOptions={[10, 25, 50]}
          tableStyle={{ minWidth: "100%" }}
          loading={props.isLoading}
          filters={filters}
          globalFilterFields={["name"]}
          onFilter={(e: DataTableStateEvent) => setFilters(e.filters)}
          size="small"
          emptyMessage={t(translationKeys.adminArea.glossary.noGlossariesFound)}
        >
          <Column
            field="name"
            style={{ minWidth: "250px" }}
            sortable
            header={t(glossaryTranslationKeys.glossaryTable.glossary.name)}
          />

          <Column field="description" sortable header={t(glossaryTranslationKeys.glossaryTable.glossary.description)} />

          <Column
            field="supportedLanguages"
            sortable
            header={t(glossaryTranslationKeys.glossaryTable.glossary.supportedLanguages)}
            body={(rowData: IGlossaryFileSupportExtended) => supportedLanguagesTemplate(rowData.supportedLanguages)}
            filter
            showFilterMenu
            showFilterMatchModes={false}
            filterElement={SupportedLanguagesFilterElement}
          />

          <Column
            style={{ width: "80px" }}
            headerStyle={{ width: "80px" }}
            frozen
            alignFrozen="right"
            body={(rowData: IGlossaryFileSupportExtended) => (
              <div className="glossary-actions">
                <EditSecurityGroupButton glossary={rowData} onUpdatedGlossary={props.onReloadTable} />

                <ColumnActionIcon
                  rowData={rowData}
                  icon={faFileDownload}
                  tooltipTranslationKey={glossaryTranslationKeys.actions.downloadGlossary}
                  executeAction={() => downloadGlossaryFile(rowData)}
                />

                <ColumnActionIcon
                  rowData={rowData}
                  icon={faTrash}
                  tooltipTranslationKey={glossaryTranslationKeys.actions.deleteGlossary}
                  className="danger"
                  confirmAction={() =>
                    confirmAction(t, {
                      acceptLabel: t(glossaryTranslationKeys.dialog.deleteGlossaryConfirmationHeader),
                      message: t(glossaryTranslationKeys.dialog.deleteGlossaryConfirmationMessage, {
                        name: rowData.name,
                      }),
                    })
                  }
                  executeAction={() => deleteGlossary(rowData)}
                />
              </div>
            )}
          />
        </DataTable>
      </div>
    );
  }, [props.glossaryInfos, props.isLoading, userStore.currentLanguage, filters]);

  return glossaryTableMemo;
};

export default GlossaryTable;
